import { useState } from 'react';
import { Edit, Trash2, MoreHorizontal, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserEditDialog } from './UserEditDialog';
import { User, Role, UserStatus } from '@/types/user';
import {
  useDeactivateUser,
  useReactivateUser,
  useResendInvitation,
} from '@/hooks/react-query/useUsers';

interface UsersListProps {
  users: User[];
  roles: Role[];
  onUserUpdated: () => void;
}

export function UsersList({ users, roles, onUserUpdated }: UsersListProps) {
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const deactivateUserMutation = useDeactivateUser();
  const reactivateUserMutation = useReactivateUser();
  const resendInvitationMutation = useResendInvitation();

  const getStatusBadge = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return (
          <Badge className="bg-emerald-500/20 text-emerald-500 dark:text-emerald-400 border-0">
            Active
          </Badge>
        );
      case UserStatus.INVITED:
        return (
          <Badge className="bg-blue-500/20 text-blue-500 dark:text-blue-400 border-0">
            Invited
          </Badge>
        );
      case UserStatus.PENDING:
        return (
          <Badge className="bg-amber-500/20 text-amber-500 dark:text-amber-400 border-0">
            Pending
          </Badge>
        );
      case UserStatus.INACTIVE:
        return <Badge variant="outline">Inactive</Badge>;
      case UserStatus.SUSPENDED:
        return (
          <Badge className="bg-red-500/20 text-red-500 dark:text-red-400 border-0">
            Suspended
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getUserRole = (user: User) => {
    if (user.roles && user.roles.length > 0) {
      return user.roles[0].display_name;
    }
    return 'No role assigned';
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };

  const handleUserEdited = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    onUserUpdated();
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      if (user.status === UserStatus.ACTIVE) {
        await deactivateUserMutation.mutateAsync(user.id);
      } else {
        await reactivateUserMutation.mutateAsync(user.id);
      }
      onUserUpdated();
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const handleResendInvitation = async (user: User) => {
    try {
      await resendInvitationMutation.mutateAsync(user.id);
      onUserUpdated();
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  No team members found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  className="hover:bg-accent cursor-pointer"
                  onClick={() => handleEditUser(user)}
                >
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-medium">
                        {getInitials(user.first_name, user.last_name)}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {user.full_name}
                        </div>
                        {user.title && (
                          <div className="text-sm text-muted-foreground">
                            {user.title}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-foreground">
                    {user.email}
                  </TableCell>
                  <TableCell>
                    <span className="text-foreground">{getUserRole(user)}</span>
                  </TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditUser(user);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        {user.status === UserStatus.INVITED && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendInvitation(user);
                            }}
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Resend Invitation
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleUserStatus(user);
                          }}
                          className={
                            user.status === UserStatus.ACTIVE
                              ? 'text-red-600'
                              : 'text-green-600'
                          }
                        >
                          {user.status === UserStatus.ACTIVE ? (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <Edit className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit User Dialog */}
      <UserEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        user={editingUser}
        roles={roles}
        onUserEdited={handleUserEdited}
      />
    </>
  );
}
